import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../auth/AuthContext";
import { DEPARTMENTS, DEFAULT_DEPARTMENT, isAdminRole, normalizeDepartment, getEnabledDepartmentsFromSession, isDepartmentEnabled } from "../constants/departments";
import { getDepartmentAccent } from "../constants/departmentBoard";
import { FiLayers } from "react-icons/fi";
import { useEnabledDepartments } from "../hooks/useEnabledDepartments";
import { COLORS as C, RADIUS } from "../constants/ui";

const STORAGE_KEY = "departmentView";

export function setStoredDepartmentView(value) {
  try {
    const v = value ? normalizeDepartment(value) : "";
    if (v && DEPARTMENTS.includes(v)) {
      localStorage.setItem(STORAGE_KEY, v);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function getStoredDepartmentView() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return "";
    const v = normalizeDepartment(raw);
    const enabled = getEnabledDepartmentsFromSession();
    if (DEPARTMENTS.includes(v) && isDepartmentEnabled(v, enabled)) {
      if (raw !== v) setStoredDepartmentView(v);
      return v;
    }
    if (raw) setStoredDepartmentView("");
    return "";
  } catch {
    return "";
  }
}

function buildDepartmentOptions(selectableDepartments) {
  return [
    { value: "", label: "All departments" },
    ...(selectableDepartments || []).map((d) => ({ value: d, label: d })),
  ];
}

function DepartmentBadge({ department, subtitle, children, trailing }) {
  const accent = getDepartmentAccent(department);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 20,
        padding: "12px 16px",
        borderRadius: 14,
        background: accent.bg,
        border: `1px solid ${accent.border}`,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: accent.iconBg,
            color: accent.color,
            flexShrink: 0,
          }}
        >
          <FiLayers size={18} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: accent.color,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {subtitle}
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>{department}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        {children}
        {trailing}
      </div>
    </div>
  );
}

/**
 * @param {boolean} liveUpdate  Skip the page reload and let subscribers react
 *   to the `departmentViewChanged` event instead. Most pages read the stored
 *   value once at render with no subscription, so they need the reload; Budget
 *   already listens for the event and re-queries in place.
 */
export default function DepartmentSwitcher({ trailing = null, liveUpdate = false }) {
  const { role, user } = useContext(AuthContext);
  const isAdmin = isAdminRole(role);
  const { selectableDepartments } = useEnabledDepartments();
  const options = buildDepartmentOptions(selectableDepartments);
  const userDepartment = normalizeDepartment(user?.department || DEFAULT_DEPARTMENT);
  const [value, setValue] = useState(() => getStoredDepartmentView());

  useEffect(() => {
    setValue(getStoredDepartmentView());
  }, []);

  if (!isAdmin) {
    return (
      <DepartmentBadge department={userDepartment} subtitle="Your department" trailing={trailing}>
        <span
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#64748b",
            maxWidth: 280,
            lineHeight: 1.4,
          }}
        >
          Projects, budget, and sprint board are scoped to this department.
        </span>
      </DepartmentBadge>
    );
  }

  const handleSelect = (next) => {
    setValue(next);
    setStoredDepartmentView(next);
    // Always announce, so anything listening stays in step regardless of mode.
    window.dispatchEvent(new CustomEvent("departmentViewChanged", { detail: next }));
    if (!liveUpdate) {
      window.location.reload();
    }
  };

  const displayDepartment = value || "All departments";
  const accent = getDepartmentAccent(value || userDepartment);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 20,
        padding: "12px 16px",
        borderRadius: RADIUS.card,
        background: C.surface,
        border: `1px solid ${C.line}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: accent.iconBg,
            color: accent.color,
            flexShrink: 0,
          }}
        >
          <FiLayers size={18} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: C.inkFaint,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Department view
          </div>
          <div style={{ fontSize: "0.88rem", fontWeight: 700, color: C.ink }}>{displayDepartment}</div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <div
        role="tablist"
        aria-label="Department view"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          padding: 5,
          borderRadius: RADIUS.card,
          background: C.sunken,
          border: `1px solid ${C.line}`,
        }}
      >
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => handleSelect(opt.value)}
              style={{
                border: "none",
                borderRadius: RADIUS.control,
                padding: "8px 14px",
                fontSize: "0.8rem",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
                background: active ? accent.color : "transparent",
                color: active ? "#ffffff" : C.inkSoft,
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
        {trailing}
      </div>
    </div>
  );
}
